import React, { useState } from "react";
import { UserAutocomplete, User } from "../UserAutocomplete";
import { useReplaceMarathonParticipantMutation } from "../../api/marathonAdmin";
import { AdminMarathonParticipant } from "../../types/marathonAdmin";

interface ReplaceParticipantModalProps {
    isOpen: boolean;
    onClose: () => void;
    participant: AdminMarathonParticipant | null;
    marathonId: number;
    onSuccess?: () => void;
}

export const ReplaceParticipantModal: React.FC<ReplaceParticipantModalProps> = ({
    isOpen,
    onClose,
    participant,
    marathonId,
    onSuccess,
}) => {
    const [selectedUserId, setSelectedUserId] = useState<string>("");
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [replaceParticipant, { isLoading }] = useReplaceMarathonParticipantMutation();

    if (!isOpen || !participant) return null;

    const handleReplace = async () => {
        if (!selectedUser || !participant) return;

        try {
            await replaceParticipant({
                marathonId,
                participantId: participant.id,
                newUserId: selectedUser.id,
            }).unwrap();

            setSelectedUserId("");
            setSelectedUser(null);
            onSuccess?.();
            onClose();
        } catch (error) {
            console.error("Ошибка при замене участника:", error);
            alert(
                error && typeof error === "object" && "data" in error
                    ? (error.data as { message?: string })?.message ||
                          "Ошибка при замене участника"
                    : "Ошибка при замене участника"
            );
        }
    };

    const handleCancel = () => {
        setSelectedUserId("");
        setSelectedUser(null);
        onClose();
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-2xl w-full mx-4">
                {/* Заголовок */}
                <div className="flex justify-between items-center p-6 border-b border-gray-200 dark:border-gray-700">
                    <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                        Замена участника
                    </h2>
                    <button
                        onClick={handleCancel}
                        className="text-gray-400 hover:text-gray-600 dark:text-gray-400 transition-colors"
                        disabled={isLoading}
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                {/* Содержимое */}
                <div className="p-6 space-y-6">
                    {/* Информация о текущем участнике */}
                    <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
                        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                            Текущий участник:
                        </h3>
                        <div className="space-y-2 text-sm">
                            <div>
                                <span className="font-medium text-gray-900 dark:text-white">
                                    Имя:
                                </span>{" "}
                                <span className="text-gray-700 dark:text-gray-300">
                                    {participant.user?.name ?? "—"}
                                </span>
                            </div>
                            <div>
                                <span className="font-medium text-gray-900 dark:text-white">
                                    ID:
                                </span>{" "}
                                <span className="text-gray-700 dark:text-gray-300">
                                    {participant.user?.id ?? "—"}
                                </span>
                            </div>
                            {participant.user?.secret_cookie && (
                                <div>
                                    <span className="font-medium text-gray-900 dark:text-white">
                                        Cookie:
                                    </span>{" "}
                                    <span className="text-gray-700 dark:text-gray-300">
                                        {participant.user.secret_cookie}
                                    </span>
                                </div>
                            )}
                            <div>
                                <span className="font-medium text-gray-900 dark:text-white">
                                    Внесенная сумма:
                                </span>{" "}
                                <span className="text-gray-700 dark:text-gray-300">
                                    {participant.amount}
                                </span>
                            </div>
                        </div>
                    </div>

                    {/* Поиск нового пользователя */}
                    <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Новый участник:
                        </label>
                        <UserAutocomplete
                            value={selectedUserId}
                            onChange={setSelectedUserId}
                            onUserSelect={setSelectedUser}
                            placeholder="ID, email, phone, name..."
                            disabled={isLoading}
                        />
                    </div>
                </div>

                {/* Кнопки */}
                <div className="flex justify-end space-x-3 p-6 border-t border-gray-200 dark:border-gray-700">
                    <button
                        onClick={handleCancel}
                        disabled={isLoading}
                        className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Отмена
                    </button>
                    <button
                        onClick={handleReplace}
                        disabled={isLoading || !selectedUser}
                        className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        {isLoading ? "Замена..." : "Заменить"}
                    </button>
                </div>
            </div>
        </div>
    );
};

